import { z } from "zod";
import { LocalDate, ScheduleSeriesView } from "./schedule.ts";

/**
 * Завершение серии пары с даты: `POST /v1/schedule/series/:seriesId/end`.
 * Разбор в `docs/schedule-design.md`, раздел 5.6.
 *
 * `from` это ДАТА в зоне серии, «занятий с этого дня нет». Без `from` сервер
 * берёт самую раннюю дату, которая не трогает начавшиеся занятия и проведённые
 * уроки. Серия без истории раньше `from` удаляется целиком, с историей
 * закрывается датой (`ends_on = from - 1`).
 *
 * ⚠️ **`force: true` требует ОБА поля, `expectLessonIds` и `expectDeletes`**,
 * иначе `400 invalid_body`. Пустой массив `[]` считается переданным: человек
 * подтвердил список, в котором уроков не было. Без этих полей `force` означал бы
 * «удали всё, что найдёшь», в том числе урок, подготовленный в соседней вкладке
 * уже после показа подтверждения, или удаление вместо закрытия датой.
 */
export const EndSeriesRequest = z.object({
  from: LocalDate.optional(),
  force: z.boolean().optional(),
  /**
   * Уроки показанного списка. С `force` обязателен, `[]` это «уроков не было».
   *
   * Потолок 10 000, а не привычные 500: список собирает сервер из ВСЕХ будущих
   * уроков серии, и потолок ниже возможного их числа давал бы `400` без выхода.
   * Десять тысяч это 14 слотов в неделю на 13 лет вперёд, около 370 КБ тела, в
   * пределах лимита тела Fastify.
   */
  expectLessonIds: z.array(z.string().uuid()).max(10_000).optional(),
  /** Что показали человеку: удаление или закрытие датой. С `force` обязателен. */
  expectDeletes: z.boolean().optional(),
});
export type EndSeriesRequest = z.infer<typeof EndSeriesRequest>;

/**
 * Что удержало конец серии позже запрошенного: начавшееся занятие или
 * проведённый урок. `on` это дата МЕСТА (её человек видит в сетке), а не
 * исходная дата перенесённого занятия.
 *
 * ⚠️ `kind` это закрытое перечисление: новое значение потребует того же
 * порядка, что всякое расширение `z.enum` на проводе, сначала клиент, умеющий
 * его разобрать, потом сервер, который его шлёт.
 */
export const HeldByView = z.object({ kind: z.enum(["started", "lesson"]), on: LocalDate });
export type HeldByView = z.infer<typeof HeldByView>;

/**
 * Итог завершения.
 *
 * - `ended`: серия закрыта датой. `endsOn` новый конец, `lastOn` дата места
 *   последнего оставшегося занятия (или `null`, если их нет), `series` свежий вид
 * - `deleted`: серии больше нет, `heldBy`, `endsOn`, `lastOn`, `series` пусты
 * - `unchanged`: серия и так кончалась не позже, записи не было. `endsOn`,
 *   `lastOn`, `series` нынешние, `heldBy` пуст
 *
 * ⚠️ `outcome` закрытое перечисление, порядок расширения тот же, что у `kind`
 * в `HeldByView`.
 */
export const EndSeriesResponse = z.object({
  outcome: z.enum(["ended", "deleted", "unchanged"]),
  heldBy: HeldByView.nullable(),
  endsOn: LocalDate.nullable(),
  lastOn: LocalDate.nullable(),
  series: ScheduleSeriesView.nullable(),
});
export type EndSeriesResponse = z.infer<typeof EndSeriesResponse>;

/**
 * Детали `409 series_end_confirm`: что пропадёт, если подтвердить.
 *
 * - `deletes`: серия удалится целиком (истории раньше `from` нет), иначе
 *   закроется датой. Подтверждение с `force` обязано вернуть его в
 *   `expectDeletes`
 * - `lessons`: уроки, которые уйдут в корзину, `on` дата места
 * - `paidAhead`: даты оплаченных занятий не раньше сегодня
 * - `paidBefore`: даты оплаченных прошедших занятий, к которым не было урока.
 *   Бывают только при `deletes`
 * - `otherMarks`: отметки без оплаты (только посещение)
 * - `chatMessages`: сообщения чата этих занятий, удалённые не считаются
 */
export const SeriesEndConfirmDetails = z.object({
  deletes: z.boolean(),
  lessons: z.array(z.object({ id: z.string().uuid(), title: z.string(), on: LocalDate })),
  paidAhead: z.array(LocalDate),
  paidBefore: z.array(LocalDate),
  otherMarks: z.number().int().nonnegative(),
  chatMessages: z.number().int().nonnegative(),
});
export type SeriesEndConfirmDetails = z.infer<typeof SeriesEndConfirmDetails>;

/** Детали `409 series_end_past`: самая ранняя дата, с которой завершить можно. */
export const SeriesEndPastDetails = z.object({ from: LocalDate });
export type SeriesEndPastDetails = z.infer<typeof SeriesEndPastDetails>;

/**
 * Детали `409 lesson_started` у этой ручки: проведённый урок держит конец.
 * `occursOn` дата ключа занятия, `on` дата места урока, `from` самая ранняя
 * дата, с которой завершить можно.
 */
export const SeriesEndLessonStartedDetails = z.object({
  lessonId: z.string().uuid(),
  occursOn: LocalDate,
  on: LocalDate,
  from: LocalDate,
});
export type SeriesEndLessonStartedDetails = z.infer<typeof SeriesEndLessonStartedDetails>;
